/* global BigInt */
import React, { useState, useRef } from 'react';
import { Form, Row, Col, Button, Alert, Modal } from 'react-bootstrap';
import CryptoJS from 'crypto-js';
import JSZip from 'jszip';
import ApiMServer from 'services/ApiMServer';

const modPow = (base, exponent, modulus) => {
  if (modulus === 1n) return 0n;
  let result = 1n;
  let b = base % modulus;
  let e = exponent;
  while (e > 0n) {
    if (e % 2n === 1n) {
      result = (result * b) % modulus;
    }
    e = e / 2n;
    b = (b * b) % modulus;
  }
  return result;
};

const FileUpload = (props) => {
  const {
    msg,
    type,
    company,
    permission,
    handleGetChart,
    handleAlertModalError,
    handleAlertModalSuccess,
  } = props;

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isFileValid, setIsFileValid] = useState(null); // null: unknown, true: valid, false: invalid
  const [validationMessage, setValidationMessage] = useState('');
  const [validationVariant, setValidationVariant] = useState('info');
  const [parsedChartPack, setParsedChartPack] = useState(null);
  const fileInputRef = useRef(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    setSelectedFile(file || null);
    setFileName(file?.name || '');
    // reset previous validation state
    setIsFileValid(null);
    setParsedChartPack(null);
    if (file) {
      await validateSelectedFile(file);
    } else {
      setValidationMessage('');
      setValidationVariant('info');
    }
  };

  const validateSelectedFile = async (file) => {
    try {
      console.log(`[Validando] Arquivo: ${file.name}, Tipo: ${type}`);
      setValidationMessage('Validando arquivo...');
      setValidationVariant('info');

      const arrayBuffer = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);

      const curvaEntry = zip.file('curva.txt');
      const curvaSignin = zip.file('assinatura.txt');

      if (!curvaSignin || !curvaEntry) {
        setIsFileValid(false);
        setValidationMessage('Arquivo inválido: assinatura ou curva ausente.');
        setValidationVariant('danger');
        return;
      }

      const signin_date_raw = await curvaSignin.async('string');
      const signin_date = signin_date_raw.replace(/\r\n/g, '\n');
      
      const fields = extractSignatureFields(signin_date);
      const { n_hex: nFile, e_hex: eFile, date: CHART_DATE, sha256_hex: hashInFile } = fields;

      if (!CHART_DATE) {
        setIsFileValid(false);
        setValidationMessage('Assinatura: não foi possível extrair a DATA do arquivo.');
        setValidationVariant('danger');
        return;
      }

      // Referência do .env baseada no type
      let nRef = "";
      if (type === "INMETRO") {
        nRef = (process.env.REACT_APP_HASH_INMETRO || "").replace(/[\s"']/g, '').toLowerCase();
      } else if (type === "COMPLEMENT") {
        nRef = (process.env.REACT_APP_HASH_COMPLEMENT || "").replace(/[\s"']/g, '').toLowerCase();
      }

      const abbrHex = (s) => {
        s = (s || "").trim().toLowerCase();
        if (s.length <= 27) return s;
        return `${s.slice(0, 12)}...${s.slice(-12)}`;
      };

      // Validação da Chave Pública (Exatamente como no Equipamento)
      if (nFile && nRef && BigInt('0x' + nFile) !== BigInt('0x' + nRef)) {
        setIsFileValid(false);
        setValidationMessage(`Assinatura inválida: chave pública não confere (N diferente). N_arquivo=${abbrHex(nFile)}`);
        setValidationVariant('danger');
        console.error(`N_arquivo=${nFile} N_ref=${nRef}`);
        return;
      }

      if (!nFile) {
        setIsFileValid(false);
        setValidationMessage("Assinatura: não foi possível extrair N da chave pública.");
        setValidationVariant('danger');
        return;
      }

      if (eFile && parseInt(eFile, 16) !== 65537) {
        setIsFileValid(false);
        setValidationMessage(`Assinatura inválida: expoente diferente (e=${eFile}).`);
        setValidationVariant('danger');
        return;
      }

      const curvaBytes = await curvaEntry.async('uint8array');
      const decoder = new TextDecoder('iso-8859-1');
      const latin1_curva = decoder.decode(curvaBytes);

      try {
        const sigBigInt = BigInt('0x' + fields.signature_hex);
        const eBigInt = BigInt('0x' + (eFile || "010001"));
        const nBigInt = BigInt('0x' + nFile);

        const decryptedBigInt = modPow(sigBigInt, eBigInt, nBigInt);
        const decryptedHex = decryptedBigInt.toString(16).toLowerCase();

        // 1. Hash dos bytes puros (Raw) usando WordArray direto (mais fiel ao 'rb' do Python)
        const wordDataRaw = CryptoJS.lib.WordArray.create(curvaBytes);
        const hashRaw = CryptoJS.SHA256(wordDataRaw).toString(CryptoJS.enc.Hex);

        // 2. Hash da string decodificada como UTF-8 com normalização de quebra de linha
        const utf8Decoder = new TextDecoder('utf-8');
        const utf8String = utf8Decoder.decode(curvaBytes);
        const utf8StringLf = utf8String.replace(/\r\n/g, '\n');
        
        const possibleHashes = {
          "SHA256 (Puros)": hashRaw,
          "SHA256 (UTF8+LF)": CryptoJS.SHA256(utf8StringLf).toString(CryptoJS.enc.Hex),
          "SHA1 (Puros)": CryptoJS.SHA1(wordDataRaw).toString(CryptoJS.enc.Hex),
          "SHA1 (UTF8+LF)": CryptoJS.SHA1(utf8StringLf).toString(CryptoJS.enc.Hex),
          "Hash Declarado": hashInFile
        };

        const isValid = Object.values(possibleHashes).some(h => h && decryptedHex.includes(h.toLowerCase()));

        if (!isValid) {
          console.log("--- TESTE FINAL DE HASH ---");
          console.log("Hash Desejado:", decryptedHex.slice(-64));
          console.log("Hashes Tentados:", possibleHashes);
          
          setIsFileValid(false);
          setValidationMessage(`Assinatura inválida: verificação criptográfica falhou. sha256_arquivo=${abbrHex(hashInFile)}`);
          setValidationVariant('danger');
          return;
        }
      } catch (e) {
        console.error("Erro na decriptação RSA:", e);
        setIsFileValid(false);
        setValidationMessage('Erro ao processar decriptação da assinatura.');
        setValidationVariant('danger');
        return;
      }

      const chartLines = latin1_curva.split(/\r?\n/);
      const chartPack = convertChartsToJson(chartLines, hashInFile, CHART_DATE, type);
      setParsedChartPack(chartPack);

      setIsFileValid(true);
      setValidationMessage('Arquivo válido. Pronto para enviar.');
      setValidationVariant('success');
    } catch (err) {
      console.error('Erro ao validar arquivo:', err);
      setIsFileValid(false);
      setValidationMessage('Arquivo inválido ou corrompido.');
      setValidationVariant('danger');
    }
  };

  const handleConfirm = () => {
    setShowConfirm(true);
  }

  const handleConfirmProceed = async () => {
    setShowConfirm(false);
    await handleUpload();
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      alert("Pasta não selecionada!")
      return;
    }

    if (isFileValid === false) {
      alert('Arquivo inválido. Verifique e tente novamente.');
      return;
    }

    try {
      if (parsedChartPack) {
        await handleSubmitChartPack(parsedChartPack);
      } else {
        const arrayBuffer = await selectedFile.arrayBuffer();
        const zip = await JSZip.loadAsync(arrayBuffer);
        const curvaEntry = zip.file('curva.txt');
        const curvaSignin = zip.file('assinatura.txt');

        const signin_date_raw = await curvaSignin.async('string');
        const signin_date = signin_date_raw.replace(/\r\n/g, '\n');
        const fields = extractSignatureFields(signin_date);
        const { n_hex: nFile, e_hex: eFile, date: CHART_DATE, sha256_hex: hashInFile } = fields;

        const curvaBytes = await curvaEntry.async('uint8array');
        const decoder = new TextDecoder('iso-8859-1');
        const latin1_curva = decoder.decode(curvaBytes);

        let nRef = "";
        if (type === "INMETRO") {
          nRef = (process.env.REACT_APP_HASH_INMETRO || "").replace(/[\s"']/g, '').toLowerCase();
        } else if (type === "COMPLEMENT") {
          nRef = (process.env.REACT_APP_HASH_COMPLEMENT || "").replace(/[\s"']/g, '').toLowerCase();
        }

        if (nFile && nRef && BigInt('0x' + nFile) !== BigInt('0x' + nRef)) {
          alert(`Assinatura inválida: chave pública não confere.`);
          return;
        }

        try {
          const sigBigInt = BigInt('0x' + fields.signature_hex);
          const eBigInt = BigInt('0x' + (eFile || "010001"));
          const nBigInt = BigInt('0x' + nFile);

          const decryptedBigInt = modPow(sigBigInt, eBigInt, nBigInt);
          const decryptedHex = decryptedBigInt.toString(16).toLowerCase();

          const wordDataRaw = CryptoJS.lib.WordArray.create(curvaBytes);
          const utf8Decoder = new TextDecoder('utf-8');
          const utf8String = utf8Decoder.decode(curvaBytes);
          const utf8StringLf = utf8String.replace(/\r\n/g, '\n');

          const hashes = [
            CryptoJS.SHA256(wordDataRaw).toString(CryptoJS.enc.Hex),
            CryptoJS.SHA256(utf8StringLf).toString(CryptoJS.enc.Hex),
            CryptoJS.SHA1(wordDataRaw).toString(CryptoJS.enc.Hex),
            CryptoJS.SHA1(utf8StringLf).toString(CryptoJS.enc.Hex),
            hashInFile
          ];

          if (!hashes.some(h => h && decryptedHex.includes(h.toLowerCase()))) {
            alert("Erro no hash - Assinatura RSA inválida");
            return;
          }
        } catch (e) {
          alert("Erro fatal processando a decriptação RSA");
          return;
        }
        const chartLines = latin1_curva.split(/\r?\n/);
        const chartPack = convertChartsToJson(chartLines, hashInFile, CHART_DATE, type);
        await handleSubmitChartPack(chartPack);
      }
    } catch (error) {
      console.error("Error processing file:", error);
    }
  };

  const extractSignatureFields = (text) => {
    const cleanHex = (s) => (s || "").replace(/[^0-9a-fA-F]/g, "").toLowerCase();
    
    let sha256_hex = "";
    let signature_hex = "";
    let n_hex = "";
    let e_hex = "";
    let date = "";

    const mSha = /SHA256\s+do\s+arquivo\s*\n([0-9a-fA-F\s]+)/i.exec(text);
    if (mSha) sha256_hex = cleanHex(mSha[1]);

    const mSigBlock = /Assinatura\s+digital[^\n]*\n([\s\S]+?)\nChave\s+Pública/i.exec(text);
    if (mSigBlock) signature_hex = cleanHex(mSigBlock[1]);

    const mPkBlock = /Chave\s+Pública[^\n]*\n([\s\S]+)/i.exec(text);
    if (mPkBlock) {
      const pkLines = mPkBlock[1].split("\n");
      let i = 0;
      while (i < pkLines.length) {
        if (pkLines[i].trim().toUpperCase() === "N") {
          i++;
          let nParts = [];
          while (i < pkLines.length) {
            let line = pkLines[i].trim();
            if (line.toLowerCase() === "e") break;
            if (line && /^[0-9a-fA-F\s]+$/.test(line)) nParts.push(line);
            i++;
          }
          n_hex = cleanHex(nParts.join(""));
          if (i < pkLines.length && pkLines[i].trim().toLowerCase() === "e") {
            i++;
            while (i < pkLines.length) {
              let line = pkLines[i].trim();
              if (line && /^[0-9a-fA-F]+$/.test(line)) {
                e_hex = cleanHex(line);
                break;
              }
              i++;
            }
          }
          break;
        }
        i++;
      }
    }

    const mDate = /\bData:\s*([0-9]{2}\/[0-9]{2}\/[0-9]{4})\b/i.exec(text);
    if (mDate) date = mDate[1];

    return { sha256_hex, signature_hex, n_hex, e_hex, date };
  };

  const getSubstrings = (stringSubject, string1, string2) => {
    let myList = [];
    let intstart = 0;
    const strlength = stringSubject.length;
    let continueloop = true;

    while (intstart < strlength && continueloop) {
      let intindex1 = stringSubject.indexOf(string1, intstart);
      if (intindex1 !== -1) {
        intindex1 += string1.length;
        let intindex2 = stringSubject.indexOf(string2, intindex1);
        if (intindex2 !== -1) {
          const subsequence = stringSubject.substring(intindex1, intindex2);
          myList.push(subsequence);
          intstart = intindex2 + string2.length;
        } else {
          continueloop = false;
        }
      } else {
        continueloop = false;
      }
    }
    return myList;
  };

  const convertChartsToJson = (chart, HASH, CHART_DATE, CHART_TYPE) => {
    let curvas = [];

    for (let line of chart) {
      if (!line) continue;
      let s = line.trim();
      let l = new Set(getSubstrings(s, "<", ">"));
      let j = {};

      l.forEach(i => {
        const regex = new RegExp(`<${i}>(.*?)<${i}>`);
        const result = s.match(regex);
        if (result) {
          let val = result[1];
          if (val.includes(";")) {
            j[i] = val.split(";").map(v => v.trim());
          } else {
            j[i] = val.trim();
          }
        }
      });

      j._id = CryptoJS.SHA256(JSON.stringify(j)).toString();
      j.hash = HASH;
      j.data_pack = CHART_DATE;
      j.chart_type = CHART_TYPE;
      curvas.push(j);
    }
    return curvas;
  };

  const handleSubmitChartPack = async (chartPack) => {
    try {
      const formData = new FormData();
      if (selectedFile) {
        formData.append('file', selectedFile, selectedFile.name);
      }
      formData.append('chartPack', JSON.stringify(chartPack));
      if (company !== undefined && company !== null) formData.append('company', String(company));
      if (type !== undefined && type !== null) formData.append('type', String(type));

      if (selectedFile) {
        const arrayBuffer = await selectedFile.arrayBuffer();
        const zip = await JSZip.loadAsync(arrayBuffer);
        const curvaEntry = zip.file('curva.txt');
        const curvaSignin = zip.file('assinatura.txt');

        if (curvaEntry && curvaSignin) {
          const assinaturaData = await curvaSignin.async('string');
          const curvaData = await curvaEntry.async('string');
          formData.append('assinatura', assinaturaData);
          formData.append('curva', curvaData);
        }
      }

      await ApiMServer.post(`/api/v1/upcharts`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (handleGetChart) {
        handleGetChart()
      }
      setSelectedFile(null)
      setFileName("")
      setIsFileValid(null)
      setValidationMessage("")
      setValidationVariant('info')
      setParsedChartPack(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      if (handleAlertModalSuccess) {
        handleAlertModalSuccess('Update realizado com sucesso!');
      }
    } catch (error) {
      if (handleAlertModalError) {
        handleAlertModalError('Erro ao realizar upload!');
      }
      console.log(error);
    }
  }

  return (
    <Row>
      <Col>
        <Form>
          <Form.Group controlId="formFile" className="mb-2">
            <Form.Control
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              disabled={permission === true ? false : true}
            />
          </Form.Group>
          {fileName && (
            <p className="mb-3">
              <strong>Arquivo Selecionado:</strong> {fileName}
            </p>
          )}
          {validationMessage && (
            <Alert variant={validationVariant} className="mb-3 py-2">
              {validationMessage}
            </Alert>
          )}
          <Button
            variant="success"
            onClick={handleConfirm}
            disabled={!selectedFile || isFileValid !== true}
          >
            Enviar
          </Button>
        </Form>
      </Col>
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered className="themed-modal">
        <Modal.Header closeButton>
          <Modal.Title>Confirmar envio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {msg || 'Enviar a nova curva substituirá a curva atual!'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmProceed}>
            Enviar
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

export default FileUpload;
